import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useSelector } from 'react-redux';

import { IconButton } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { FILTERS_MESSAGE_LOCALSTORAGE, useFilterMessagesContext } from 'contexts';
import { Button, Typography } from 'embeds/components';

import { useLocalStorage } from '@lib/hooks';
import { selectCurrentMasterSettings } from '@lib/selectors';

import { FilterChatActivityInput } from './__generated__/QueryListChatActivityAssignees_EmbedQuery.graphql';
import Filters, { FiltersProps } from './Filters';

const styles = ({
  spacing: { unit },
  palette: { secondary },
  custom: {
    palette: { colorVeryLightGray, colorSupportPowder },
  },
}: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      height: '100%',
      width: '100%',
    },
    header: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: colorSupportPowder,
      padding: `${unit}px ${unit}px ${unit}px ${unit * 2}px`,
    },
    headerWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    filterIcon: {
      marginLeft: unit,
      marginTop: unit / 2,
    },
    unassignedChatsIcon: {
      backgroundColor: secondary.main,
      borderRadius: '50%',
      display: 'inline-block',
      alignSelf: 'flex-start',
      marginTop: 10,
      height: 6,
      width: 6,
    },
    closeButton: {
      float: 'right',
    },
    closeButtonIcon: {
      fontSize: 16,
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: colorVeryLightGray,
      padding: unit * 3,
      bottom: 0,
      width: 370,
    },
    footerButton: {
      width: 140,
    },
  });

const messages = defineMessages({
  sendAndReceiverTitle: {
    id: 'deprecated.queries.FiltersChatActivities.sendAndReceiverTitle',
    defaultMessage: 'My Messages - Filters',
  },
});

export interface FiltersChatActivitiesProps extends WithStyles<typeof styles> {
  filters: { assignees: string[]; facilities: string[] };
  title?: string;
  includeFilterAssignees?: boolean;
  filterChatActivityCount?: FilterChatActivityInput;
  onChange: (newFilters: { assignees: string[]; facilities: string[] }) => void;
  onClear: () => void;
  onCancel: () => void;
}

const FiltersChatActivities: React.FC<FiltersChatActivitiesProps> = ({
  classes,
  filters: initialFilters,
  title = 'Filters',
  includeFilterAssignees,
  filterChatActivityCount,
  onChange,
  onClear,
  onCancel,
}) => {
  const [, setPersistFiltersMessages] = useLocalStorage(FILTERS_MESSAGE_LOCALSTORAGE, null, true);
  const { formatMessage } = useIntl();
  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseSendAndReceiverFilters = settings?.communication?.filterMessagesBySenderAndReceiver;

  const [filters, setFilters] = useState(initialFilters);

  const handleNodesChecked = useCallback(
    (filter: 'assignees' | 'facilities', value: string[]) => {
      setFilters({ ...filters, [filter]: value });
    },
    [setFilters, filters],
  );

  const { filters: filtersMessages, setFilters: setFilterMessages } = useFilterMessagesContext();

  const handleFilterMessagesChange = useCallback(
    (filter: keyof typeof filtersMessages, value: string[] | boolean) => {
      const newFiltersMessages = { ...filtersMessages, [filter]: value };
      setFilterMessages(newFiltersMessages);
      setPersistFiltersMessages(newFiltersMessages);
    },
    [setFilterMessages, setPersistFiltersMessages, filtersMessages],
  );

  const handleSubmit = () => {
    onChange(filters);
  };

  const filterMessagesConfig = useMemo(
    () => ({
      ...(includeFilterAssignees && {
        chatActivityAssignees: {
          title: 'Assignees',
          defaultExpanded: true,
          stylePanelDetails: {
            display: 'inline',
          },
          componentProps: {
            variables: {
              includeAvatar: true,
              includeChatActivityCount: true,
              filterChatActivityCount,
            },
            idsChecked: filters.assignees,
            onNodesChecked: (_: unknown, ids: string[]) => handleNodesChecked('assignees', ids),
          },
        },
      }),
      me: {
        title: 'Me',
        defaultExpanded: true,
        stylePanelDetails: {
          display: 'inline',
        },
        componentProps: {
          isChecked: filtersMessages.me,
          onCheck: (value: boolean) => handleFilterMessagesChange('me', value),
        },
      },
      facilities: {
        title: 'Facilities',
        defaultExpanded: true,
        stylePanelDetails: {
          display: 'inline',
        },
        componentProps: {
          variables: {},
          idsChecked: filtersMessages.facilities,
          onNodesChecked: (_: unknown, ids: string[]) => handleFilterMessagesChange('facilities', ids),
        },
      },
      groups: {
        title: 'Groups',
        defaultExpanded: true,
        stylePanelDetails: {
          display: 'inline',
        },
        componentProps: {
          variables: {},
          idsChecked: filtersMessages.groups,
          onNodesChecked: (_: unknown, ids: string[]) => handleFilterMessagesChange('groups', ids),
        },
      },
    }),
    [
      filters.assignees,
      includeFilterAssignees,
      filtersMessages.me,
      filtersMessages.facilities,
      filtersMessages.groups,
      handleFilterMessagesChange,
      handleNodesChecked,
      filterChatActivityCount,
    ],
  );

  const filtersConfig = useMemo(() => {
    const result: FiltersProps['filters'] = {};
    if (includeFilterAssignees) {
      result.chatActivityAssignees = {
        title: 'Assignees',
        defaultExpanded: true,
        stylePanelDetails: {
          display: 'inline',
        },
        componentProps: {
          // @ts-ignore
          variables: {
            includeAvatar: true,
            includeChatActivityCount: true,
            filterChatActivityCount,
          },
          idsChecked: filters.assignees,
          onNodesChecked: (_: unknown, ids: string[]) => handleNodesChecked('assignees', ids),
        },
      };
    }
    result.facilities = {
      title: 'Facilities',
      defaultExpanded: true,
      stylePanelDetails: {
        display: 'inline',
      },
      componentProps: {
        // @ts-ignore
        variables: {},
        idsChecked: filters.facilities,
        onNodesChecked: (_: unknown, ids: string[]) => handleNodesChecked('facilities', ids),
      },
    };
    return result;
  }, [includeFilterAssignees, filters.facilities, filters.assignees, filterChatActivityCount, handleNodesChecked]);

  return (
    <div className={classes.container}>
      <>
        <div className={classes.header}>
          <div className={classes.headerWrapper}>
            <Typography variant="body1StrongBold">
              {isToUseSendAndReceiverFilters ? formatMessage(messages.sendAndReceiverTitle) : title}
            </Typography>
          </div>
          <IconButton
            data-lh-id="filter-chat-activities-close-icon-button"
            onClick={() => {
              if (isToUseSendAndReceiverFilters) {
                handleSubmit();
              } else {
                onCancel();
              }
            }}
            className={classes.closeButton}
          >
            <CloseIcon className={classes.closeButtonIcon} />
          </IconButton>
        </div>

        {/* @ts-ignore */}
        <Filters filters={isToUseSendAndReceiverFilters ? filterMessagesConfig : filtersConfig} />
      </>

      <div className={classes.footer}>
        <Button
          data-lh-id="filter-chat-activities-clear-button"
          onClick={() => {
            onClear();
            setFilters({ assignees: [], facilities: [] });
            setFilterMessages({ facilities: [], groups: [], me: false });
          }}
          className={classes.footerButton}
          variant="contained"
        >
          Clear
        </Button>
        <Button
          data-lh-id="filter-chat-activities-apply-button"
          className={classes.footerButton}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(FiltersChatActivities);
